import { assertEvent } from 'xstate5';

import { getModificationsAfterSplit } from '../../helpers';
import { type Guard, type Modification, type SplitPortionAction } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if the split portion operation would exceed the maximum aggregate quantity
 * for the modification group.
 */
export const checkIfSplitWouldExceedMaxAggregateQuantity: Guard<Params> = function (
  args,
  params,
): boolean {
  const { action } = params ?? { action: 'toggle' };
  const { context, event } = args;

  assertEvent(event, [
    'customization.toggle-split-portion',
    'customization.remove-split-portion-completely',
  ]);

  const { ingredientId } = event;
  const { allModificationsMap, modifications, modificationLimits } = context;

  const modification = allModificationsMap?.get(ingredientId);

  // ─── Missing Data ──────────────────────────────────────────────────────────

  const hasMissingData =
    !modifications || !modification || !allModificationsMap || !modificationLimits;

  if (hasMissingData) {
    return false;
  }

  // ───────────────────────────────────────────────────────────────────────────

  const { modificationGroupName } = modification;
  const { active } = modifications;

  const getSameGroupModifications = getSameGroupModificationsFactory(modificationGroupName); // prettier-ignore

  const modificationGroupLimits = modificationLimits[modificationGroupName];
  const maxAggregateQuantity = modificationGroupLimits?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY; // prettier-ignore

  const updatedActive = getModificationsAfterSplit({
    activeModifications: active,
    ingredientId,
    modificationGroupName,
    modificationLimits,
    allModificationsMap,
    action,
  });

  const sameGroupModifications = updatedActive.filter(getSameGroupModifications); // prettier-ignore
  const totalPortionsAfterSplit = sameGroupModifications.reduce(sumQuantity, 0);

  return totalPortionsAfterSplit > maxAggregateQuantity;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSameGroupModificationsFactory(modificationGroupName: string) {
  return (modification: Modification) => modification.modificationGroupName === modificationGroupName; // prettier-ignore
}

function sumQuantity(total: number, modification: Modification) {
  return total + modification.quantity;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  action: SplitPortionAction;
};
